import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  useTheme
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CloseIcon from '@mui/icons-material/Close';
import { LoadLocationForm, Material, MaterialTonnage } from '../types';

interface LocationFormModalProps {
  open: boolean;
  mode: "create" | "edit";
  initialData?: LoadLocationForm;
  materials: Material[];
  onClose: () => void;
  onSubmit: (data: LoadLocationForm) => void;
}

const commonTextFieldStyles = {
  mb: 2,
  backgroundColor: 'white',
  borderRadius: 1
};

const AddNewLocationPopUp: React.FC<LocationFormModalProps> = ({
  open,
  mode,
  initialData,
  materials,
  onClose,
  onSubmit
}) => {
  const theme = useTheme();
  const [form, setForm] = useState<LoadLocationForm>({
    level1: "",
    level2: "",
    level3: "",
    level4: "",
    isActive: true,
    materialsTonnage: []
  });

  const [errors, setErrors] = useState<string[]>([]);

  // Load initial data for edit mode
  useEffect(() => {
    setErrors([]);
    if (mode === "edit" && initialData) {
      setForm(initialData);
    }
    else {
      setForm({
        level1: "",
        level2: "",
        level3: "",
        level4: "",
        isActive: true,
        materialsTonnage: []
      });
    }
  }, [mode, initialData]);

  const updateField = (field: keyof LoadLocationForm, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const updateMaterialRow = (index: number, field: keyof MaterialTonnage, value: any) => {
    const updated = [...form.materialsTonnage];
    updated[index] = { ...updated[index], [field]: value };
    setForm(prev => ({ ...prev, materialsTonnage: updated }));
  };

  const addMaterialRow = () => {
    if (form.materialsTonnage.length >= materials.length) {
      setErrors(["You cannot add more materials than what is available"]);
      return;
    }

    setForm(prev => ({
      ...prev,
      materialsTonnage: [...prev.materialsTonnage, { materialId: 0, tonnage: 0 }]
    }));
  };


  const removeMaterialRow = (index: number) => {
    const updated = [...form.materialsTonnage];
    updated.splice(index, 1);
    setForm(prev => ({ ...prev, materialsTonnage: updated }));
  };

  const validateForm = () => {
    const validationErrors: string[] = [];
    const materialIds = form.materialsTonnage.map(mt => mt.materialId);
    const duplicates = materialIds.filter((id, idx) => id !== 0 && materialIds.indexOf(id) !== idx);

    if (!form.level1.trim()) {
      validationErrors.push("Level 1 (Site Name) is required");
    }

    if (!form.level2.trim()) {
      validationErrors.push("Level 2 (City) is required");
    }

    if (form.materialsTonnage.length === 0) {
      validationErrors.push("At least one material must be added");
    }

    form.materialsTonnage.forEach((mt, index) => {
      if (!mt.materialId) {
        validationErrors.push(`Material selection is required for row ${index + 1}`);
      }
      if (!mt.tonnage || mt.tonnage <= 0) {
        validationErrors.push(`Tonnage must be greater than 0 for row ${index + 1}`);
      }
    });

    if (duplicates.length > 0) {
        validationErrors.push("Each material can only be selected once");
      }

    setErrors(validationErrors);
    return validationErrors.length === 0;
  };

  const handleSubmitClick = () => {
    if (!validateForm()) return;
    onSubmit(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            <LocationOnIcon /> {mode === "create" ? "Add New Load Location" : "Edit Load Location"}
          </Typography>
          <IconButton onClick={onClose} sx={{ color: theme.palette.text.primary }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: theme.palette.background.paper }}>
        {errors.length > 0 && (
          <Box
            sx={{
              backgroundColor: '#fee2e2',
              border: '1px solid #ef4444',
              color: '#b91c1c',
              padding: 2,
              borderRadius: 1,
              mb: 2
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Please fix the following errors:
            </Typography>

            {errors.map((err, idx) => (
              <Typography key={idx} variant="body2">
                • {err}
              </Typography>
            ))}
          </Box>
        )}
        <Paper elevation={0} sx={{ p: 2, backgroundColor: theme.palette.background.paper, borderRadius: 2 }}>
          <Grid container spacing={3}>
            {/* Left Column */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Level 1 (Site Name)"
                variant="outlined"
                sx={commonTextFieldStyles}
                value={form.level1}
                onChange={(e) => updateField("level1", e.target.value)}
              />

              <TextField
                fullWidth
                label="Level 3 (Province/State)"
                variant="outlined"
                sx={commonTextFieldStyles}
                value={form.level3}
                onChange={(e) => updateField("level3", e.target.value)}
              />
            </Grid>

            {/* Right Column */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Level 2 (City)"
                variant="outlined"
                sx={commonTextFieldStyles}
                value={form.level2}
                onChange={(e) => updateField("level2", e.target.value)}
              />

              <TextField
                fullWidth
                label="Level 4 (Region)"
                variant="outlined"
                sx={commonTextFieldStyles}
                value={form.level4}
                onChange={(e) => updateField("level4", e.target.value)}
              />
            </Grid>

            

            {/* Dynamic Material Rows */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary, mb: 1 }}>
                Materials & Tonnage
              </Typography>

              {form.materialsTonnage.map((mt, index) => (
                <Box key={index} display="flex" gap={2} mb={2}>
                  <TextField
                    select
                    fullWidth
                    label="Material"
                    variant="outlined"
                    sx={commonTextFieldStyles}
                    value={mt.materialId}
                    onChange={(e) => updateMaterialRow(index, "materialId", Number(e.target.value))}
                  >
                    {materials.map(m => {
                      const isSelected = form.materialsTonnage.some(
                        (row, rowIndex) => row.materialId === m.id && rowIndex !== index
                      );

                      return (
                        <MenuItem key={m.id} value={m.id} disabled={isSelected}>
                          {m.level1}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                  <TextField
                    fullWidth
                    label="Tonnage"
                    variant="outlined"
                    sx={commonTextFieldStyles}
                    type="number"
                    value={mt.tonnage}
                    onChange={(e) => updateMaterialRow(index, "tonnage", Number(e.target.value))}
                  />

                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => removeMaterialRow(index)}
                  >
                    Remove
                  </Button>
                </Box>
              ))}

              <Button variant="outlined" onClick={addMaterialRow}>
                + Add Material
              </Button>
            </Grid>
            {/* Active Checkbox */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.isActive}
                    onChange={(e) => updateField("isActive", e.target.checked)}
                  />
                }
                label="Active Location"
                sx={{ mb: 2 }}
              />
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ backgroundColor: theme.palette.background.default }}>
        <Button
          variant="contained"
          sx={{
           background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' }
              }}
          onClick={handleSubmitClick}
        >
          {mode === "create" ? "Create Location" : "Update Location"}
        </Button>

        <Button
          variant="contained"
          sx={{
           background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' }
              }}
          onClick={onClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNewLocationPopUp;